import React, { useState, useEffect } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import UserScoreModal from './UserScoreModal';

interface UserScoreButtonProps {
    userId: string;
    sportId?: string;
    buttonText?: string;
    size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
    inverted?: boolean;
    disabled?: boolean;
    fluid?: boolean;
}

export default observer(function UserScoreButton({ 
    userId, 
    sportId, 
    buttonText,
    size = 'large',
    inverted = false,
    disabled = false,
    fluid = false
}: UserScoreButtonProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isSmallScreen = window.innerWidth <= 768;
            setIsMobile(isTouchDevice && isSmallScreen);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const handleClick = async () => {
        if (disabled) return;
        
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 150));
        setOpen(true);
        setLoading(false);
    };

    const displayText = buttonText || t('userRatings.buttonText');
    const mobileSize = isMobile ? 'small' : size;

    const button = (
        <Button
            size={mobileSize}
            inverted={inverted}
            disabled={disabled}
            loading={loading}
            onClick={handleClick}
            fluid={fluid}
            style={{ 
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '4px' : '8px',
                padding: isMobile ? '8px 12px' : '10px 16px',
                background: 'white',
                color: '#2c3e50',
                border: '2px solid #e0e0e0',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                fontWeight: '600',
                position: 'relative',
                overflow: 'hidden'
            }}
            onMouseEnter={(e: { currentTarget: { style: { transform: string; boxShadow: string; borderColor: string; }; }; }) => {
                if (!disabled && !loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                    e.currentTarget.style.borderColor = '#3498db';
                }
            }}
            onMouseLeave={(e: { currentTarget: { style: { transform: string; boxShadow: string; borderColor: string; }; }; }) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = '#e0e0e0';
            }}
        >
            <span style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '8px', fontSize: '1.1em' }}>
                📊
                {!isMobile && displayText}
            </span>
        </Button>
    );

    return (
        <>
            {isMobile ? (
                button
            ) : (
                <Popup
                    content={t('userRatings.buttonTooltip')}
                    trigger={button}
                />
            )}

            <UserScoreModal
                open={open}
                onClose={() => setOpen(false)}
                userId={userId}
                sportId={sportId}
            />
        </>
    );
});